import { lazy, Suspense, useId } from 'react';
import { Activity, BarChart3, Blocks, BookOpen, FileText, Layers, Megaphone, Radio, Target, Users } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import FilterBar from './FilterBar';
import { useAnalyticsState } from './lib';
import Overview from './tabs/Overview';
import Report from './tabs/Report';
import './analytics.css';

const Pages = lazy(() => import('./tabs/Pages'));
const Visitors = lazy(() => import('./tabs/Visitors'));
const Leads = lazy(() => import('./tabs/Leads'));
const Attribution = lazy(() => import('./tabs/Attribution'));
const Cohorts = lazy(() => import('./tabs/Cohorts'));
const Explorer = lazy(() => import('./tabs/Explorer'));
const Live = lazy(() => import('./tabs/Live'));
const Guide = lazy(() => import('./tabs/Guide'));

// Two entry points over one engine: the Report (home — what every ad made you) and Traffic (the web-analytics views).
const MODES = {
  report: {
    title: 'Report',
    tabs: [
      { id: 'report', label: 'Ads', icon: Megaphone, C: Report },
      { id: 'attribution', label: 'Sources', icon: Layers, C: Attribution },
    ],
  },
  traffic: {
    title: 'Traffic',
    tabs: [
      { id: 'overview', label: 'Overview', icon: BarChart3, C: Overview },
      { id: 'pages', label: 'Pages', icon: FileText, C: Pages },
      { id: 'visitors', label: 'Visitors', icon: Users, C: Visitors },
      { id: 'leads', label: 'Leads & abandonment', icon: Target, C: Leads, registrations: true },   // webinar funnels only
      { id: 'cohorts', label: 'Cohorts', icon: Activity, C: Cohorts },
      { id: 'explorer', label: 'Report builder', icon: Blocks, C: Explorer },
      { id: 'live', label: 'Live', icon: Radio, C: Live },
      { id: 'guide', label: 'Guide', icon: BookOpen, C: Guide },
    ],
  },
};

export default function AnalyticsPage({ mode = 'report', onSelectContact }) {
  const id = useId();
  const [state, update] = useAnalyticsState();
  const m = MODES[mode] || MODES.report;
  const tabs = m.tabs.filter(t => !t.registrations || state.hasRegistrations);
  const tab = tabs.find(t => t.id === state.tab) || tabs[0];
  const Tab = tab.C;
  const activate = t => update({ tab: t.id === tabs[0].id ? null : t.id, pg: null, g: null, lv: null, pid: null, pn: null, pk: null, pc: null });
  const navigate = (e, index) => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1
      : (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    activate(tabs[next]);
    const target = e.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next];
    target.focus();
    target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  };
  return (
    <>
      <PageHeader title={m.title} />
      <div className="an-root">
        <nav className="an-tabs" role="tablist" aria-label={`${m.title} sections`}>
          {tabs.map((t, index) => (
            <button key={t.id} id={`${id}-${t.id}`} type="button" role="tab" aria-selected={t.id === tab.id}
              aria-controls={`${id}-panel`} tabIndex={t.id === tab.id ? 0 : -1} aria-current={t.id === tab.id ? 'page' : undefined}
              onClick={() => activate(t)} onKeyDown={e => navigate(e, index)}>
              <t.icon size={15} />{t.label}
            </button>
          ))}
        </nav>
        {!['live', 'guide'].includes(tab.id) && <FilterBar state={state} update={update} mode={mode} />}
        <div className="an-body" id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-${tab.id}`} tabIndex={0}>
          <Suspense fallback={<div className="an-state an-loading"><span className="sp-skeleton" /><span className="sp-skeleton" /></div>}>
            <Tab state={state} update={update} drill={update} onSelectContact={onSelectContact} />
          </Suspense>
        </div>
      </div>
    </>
  );
}
